/**
 * PRO-84 - pure helpers for reasoning about which OpenFleet environment (stack) a partner is served by.
 *
 * The management console is bound at login to a single OpenFleet stack (`store.user.ofProviderUrl`, taken from
 * POST auth/of-token for the logged-in partner) and every `pro_client` call goes to that host. The management API
 * returns, for every partner, an `environment` block describing the stack the partner lives on (`null` = unknown).
 * These helpers compare the two so the UI can label partners with their environment and block the ones that belong
 * to another stack.
 *
 * Deliberately free of React and `~/` imports so it can be unit-tested in isolation.
 */

export type PartnerEnvironment = {
  providerUrl?: string | null;
  environmentType?: string | null;
  tokenVirtualPath?: string | null;
} | null;

export type PartnerLike = {
  environment?: PartnerEnvironment;
  [key: string]: any;
};

export type AnnotatedPartner<T> = T & {
  isCurrentEnvironment: boolean;
  environmentHost: string | null;
};

const LOCAL_DEV_HOSTS = ["localhost", "127.0.0.1"];

const parseUrl = (url?: unknown): URL | null => {
  if (typeof url !== "string") return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    return new URL(trimmed);
  } catch (err) {
    return null;
  }
};

/**
 * Lower-cased `protocol//host` of a URL (the host keeps a non-default port, e.g. `http://localhost:8445`).
 * Returns "" when the URL cannot be parsed, so callers can treat "" as "unknown".
 */
export const normalizeOrigin = (url?: string | null): string => {
  const parsed = parseUrl(url);
  if (!parsed || !parsed.host) return "";

  return `${parsed.protocol}//${parsed.host}`.toLowerCase();
};

/**
 * True for localhost / 127.0.0.1 provider URLs. Mirrors the `isLocalHost` heuristic in AppStore, which also falls
 * back to a plain substring check when the value is not a well-formed URL.
 */
export const isLocalDevHost = (url?: string | null): boolean => {
  if (typeof url !== "string" || !url.trim()) return false;

  // Only trust the parser when it yielded a real hostname: "localhost:8445" parses with "localhost:" as the
  // *scheme* and an empty hostname, so it must fall through to the substring heuristic below.
  const parsed = parseUrl(url);
  if (parsed && parsed.hostname) {
    return LOCAL_DEV_HOSTS.includes(parsed.hostname.toLowerCase());
  }

  const lower = url.toLowerCase();
  return LOCAL_DEV_HOSTS.some((host) => lower.includes(host));
};

/**
 * Display host (incl. a non-default port) of a URL. Falls back to the raw trimmed string when the value is not a
 * well-formed URL so the UI still shows *something*; null when empty / missing.
 */
export const environmentHostOf = (url?: string | null): string | null => {
  if (typeof url !== "string") return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  const parsed = parseUrl(trimmed);
  if (parsed && parsed.host) return parsed.host.toLowerCase();

  return trimmed;
};

/** Host of `partner.environment.providerUrl`, or null when the partner's environment is unknown. */
export const partnerEnvironmentHost = (partner?: PartnerLike | null): string | null => {
  return environmentHostOf(partner?.environment?.providerUrl);
};

/**
 * True when the partner is served by the OpenFleet stack the console is bound to.
 * - Local development (`ofProviderUrl` on localhost / 127.0.0.1) bypasses the check entirely.
 * - Otherwise both origins must be known (non-empty) and equal; an unknown partner environment never matches.
 */
export const partnerMatchesEnvironment = (partner?: PartnerLike | null, ofProviderUrl?: string | null): boolean => {
  if (isLocalDevHost(ofProviderUrl)) return true;

  const currentOrigin = normalizeOrigin(ofProviderUrl);
  const partnerOrigin = normalizeOrigin(partner?.environment?.providerUrl);

  return currentOrigin !== "" && partnerOrigin !== "" && currentOrigin === partnerOrigin;
};

/** Returns shallow copies of the partners with `isCurrentEnvironment` and `environmentHost` filled in. */
export const annotatePartners = <T extends PartnerLike>(partners: T[], ofProviderUrl?: string | null): AnnotatedPartner<T>[] => {
  if (!Array.isArray(partners)) return [];

  return partners.map((partner) => ({
    ...partner,
    isCurrentEnvironment: partnerMatchesEnvironment(partner, ofProviderUrl),
    environmentHost: partnerEnvironmentHost(partner),
  }));
};
